import React from 'react';

import { EditIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import type { IconButtonProps } from '@vlabs/uikit/components/controls/button/icon-button';
import { toast } from 'react-toastify';

import { useGetDepartment, usePatchDepartment } from '@vlabs/api/artemy/client';
import type { DepartmentId } from '@vlabs/api/artemy/model';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { EditDepartmentGeoZoneForm } from './EditDepartmentGeoZoneForm';
import type { EditDepartmentGeoZoneFormValues } from './EditDepartmentGeoZoneForm';
import { useEditDepartmentGeoZoneTranslation } from '../i18n';

type EditDepartmentGeoZoneIconButtonProps = EditDepartmentGeoZoneProps & Omit<IconButtonProps, 'onClick'>;
interface EditDepartmentGeoZoneProps {
  departmentId: DepartmentId;
}

const displayName = 'EditDepartmentGeoZoneIconButton';
const testid = buildTestid(displayName);

function EditDepartmentGeoZoneIconButton({
  departmentId,
  'data-testid': dataTestId = undefined,
  ...props
}: EditDepartmentGeoZoneIconButtonProps): React.JSX.Element {
  const { t } = useEditDepartmentGeoZoneTranslation();
  const modal = useModal({ size: 'large' });

  const getDepartment = useGetDepartment(departmentId, {
    query: { enabled: !!departmentId },
  });

  const patchDepartment = usePatchDepartment({
    mutation: {
      onSuccess: () => {
        modal.close();
        toast.success(t('toast_success'));
      },
      onError: () => {
        toast.error(t('toast_error'));
      },
    },
  });

  const handleSubmit = (values: EditDepartmentGeoZoneFormValues): void => {
    patchDepartment.mutate({
      departmentId,
      data: { zone_coordinates: values.zoneCoordinates ?? '' },
    });
  };

  return (
    <React.Fragment>
      {modal.wrap(
        <EditDepartmentGeoZoneForm
          defaultValue={getDepartment.data?.zone_coordinates}
          onCancel={modal.close}
          onSubmit={handleSubmit}
        />,
      )}

      <IconButton
        icon={<EditIcon />}
        kind="secondary"
        onClick={modal.open}
        title={t('edit_button')}
        variant="flat"
        {...testid(dataTestId)}
        {...props}
      />
    </React.Fragment>
  );
}

EditDepartmentGeoZoneIconButton.displayName = displayName;

export { EditDepartmentGeoZoneIconButton };
